var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useAriaLive } from '@ally/use-aria-live';
import { ScreenReaderOnly } from '../ScreenReaderOnly';
import { a11y, visual } from './strings';
export var testId = {
    visibleCounter: 'mui-visible-character-counter',
    visuallyHidden: 'mui-hidden-character-counter',
};
var StyledCharacterCounter = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var align = _a.align, _b = _a.theme, colors = _b.colors, font = _b.font;
    return "\n    color: ".concat(colors['slate-5'], ";\n    && {\n      width: 100%;\n      font-size: ").concat(font.body.xs.size, ";\n      font-weight: bold;\n      text-align: ").concat(align, ";\n      text-transform: uppercase;\n    }\n  ");
});
var screenReaderAnnouncement = function (charCount, maxLength, getAriaLiveCharCount) {
    return getAriaLiveCharCount(maxLength - charCount, maxLength);
};
/**
 * Accessibility requirements:
 *
 * <=100 chars
 * at 90% of max polite "# out of # characters remaining"
 * at 100% of max assert "0 out of # characters remaining"
 *
 *  >100 chars
 * at 80% of max polite "# out of # characters remaining"
 * at 90% of max polite "# out of # characters remaining"
 * at 95% of max assert "# out of # characters remaining"
 * at 100% of max assert "0 out of # characters remaining"
 */
var CharacterCounter = function (_a) {
    var id = _a.id, charCount = _a.charCount, maxLength = _a.maxLength, _b = _a.align, align = _b === void 0 ? 'left' : _b, hideAppendedText = _a.hideAppendedText, _c = _a.getAriaLiveCharCount, getAriaLiveCharCount = _c === void 0 ? a11y.getAriaLiveCharCount : _c, _d = _a.appendedText, appendedText = _d === void 0 ? visual.appendedText : _d, others = __rest(_a, ["id", "charCount", "maxLength", "align", "hideAppendedText", "getAriaLiveCharCount", "appendedText"]);
    var _e = useAriaLive(), announcePolite = _e.announcePolite, announceAssertive = _e.announceAssertive;
    var makePoliteAnnouncement = useCallback(function () {
        announcePolite(screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, maxLength, announcePolite]);
    var makeAssertiveAnnouncement = useCallback(function () {
        announceAssertive(screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, maxLength, announceAssertive]);
    var politeBreakpoints = useMemo(function () {
        return maxLength <= 100
            ? [Math.floor(maxLength * 0.9)]
            : [Math.floor(maxLength * 0.8), Math.floor(maxLength * 0.9)];
    }, [maxLength]);
    var assertiveBreakpoints = useMemo(function () {
        return maxLength <= 100
            ? [maxLength]
            : [Math.floor(maxLength * 0.95), maxLength];
    }, [maxLength]);
    useEffect(function () {
        if (politeBreakpoints.includes(charCount)) {
            makePoliteAnnouncement();
        }
        else if (assertiveBreakpoints.includes(charCount)) {
            makeAssertiveAnnouncement();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [charCount, politeBreakpoints, assertiveBreakpoints]);
    return (React.createElement("div", null,
        React.createElement(StyledCharacterCounter, __assign({ "aria-hidden": "true", align: align, "data-testid": testId.visibleCounter }, others), "".concat(charCount, "/").concat(maxLength).concat(hideAppendedText ? '' : " ".concat(appendedText))),
        React.createElement(ScreenReaderOnly, { id: id, "data-testid": testId.visuallyHidden }, screenReaderAnnouncement(charCount, maxLength, getAriaLiveCharCount))));
};
export { CharacterCounter };
var templateObject_1;
//# sourceMappingURL=CharacterCounter.js.map